import m from "mithril"
import { MithrilTsxComponent } from "mithril-tsx-component"
import { dialog } from "oj-mithriljs-packages/dist/Dialog/Dialog"
import { cleanString } from "oj-utils"
import { IStoreItem } from "./item"
import { getTranslation } from "./translations"
import { formatTime, isStoreOpen, getNextOpen, timeMinusMargin } from "./utils"
import { Svg } from "../../svg"

declare var google: any

let isMobile: boolean
const checkIfMobile = (width: number) => {
    const _isMobile = window.innerWidth < width ? true : false
    if (isMobile === _isMobile)
        return

    isMobile = _isMobile
    m.redraw()
}

window.on("resize", () => checkIfMobile(768))

export interface IStoreDialog extends IStoreItem {
    onselect: () => void
    onclose: () => void
}
export class StoreDialog extends MithrilTsxComponent<IStoreDialog> {
    markerStore = "/FrontendWebpack/dist/assets/images/maps/Hema-marker.svg";
    markerLocation = "/FrontendWebpack/dist/assets/images/maps/marker-locatie-popup.svg";
    openHoursStr: [string, string][] = []
    orderTimeStr: string
    nowDay: string
    isOpen: boolean
    openState: string

    async oninit(v: m.Vnode<IStoreDialog>) {
        this.openHoursStr = v.attrs.days.map((x, i) => {
            const dayName = getTranslation("dayNames")[i]
            const day = v.attrs.days[i]
            return [
                dayName,
                day.openingTime
                    ? `${formatTime(day.openingTime.hour, day.openingTime.minute)} - ${formatTime(day.closingTime.hour, day.closingTime.minute)} ${getTranslation("hour")}`
                    : getTranslation("closed")
            ]
        })


        const { open, day, index } = isStoreOpen(v.attrs.days)
        this.openState = open
        this.isOpen = ["open", "future"].includes(open)
        this.nowDay = getTranslation("dayNames")[index]

        if (v.attrs.isExpressService && !v.attrs.isActive) {
            this.orderTimeStr = getTranslation("storeTempUnavailable")
        } else {
            if (this.isOpen) {
                if (v.attrs.isExpressService) {
                    this.orderTimeStr = getTranslation("pickupToday")(formatTime(...timeMinusMargin(day.closingTime.hour, day.closingTime.minute)))
                } else {
                    this.orderTimeStr = getTranslation("todayOpenUntil")(formatTime(day.closingTime.hour, day.closingTime.minute))
                }
            } else {
                const [dayName, day] = getNextOpen(v.attrs.days, index) ?? []
                if (dayName && day.openingTime) {
                    if (v.attrs.isExpressService) {
                        this.orderTimeStr = getTranslation("pickupNextDay")(dayName, formatTime(day.openingTime.hour, day.openingTime.minute))
                    } else {
                        this.orderTimeStr = getTranslation("nextDayOpenFrom")(dayName, formatTime(day.openingTime.hour, day.openingTime.minute))
                    }
                }
                else
                    this.orderTimeStr = getTranslation("closed")
            }
        }

        checkIfMobile(768)

        m.redraw()
    }

    view(v: m.Vnode<IStoreDialog>) {
        return <div className="StoreDialog">
            <button type="button" className="modal-close" onclick={v.attrs.onclose}>
                <i class="icon"><Svg src="/FrontendWebpack/dist/assets/newIcons/close.svg" /></i>
            </button>
            <div className="modal-map" oncreate={({ dom }) => this.initMap(v, dom as any)} />
            <div className="modal-storeInfo">
                <div className="modal-store-title">
                    <h2 className="modal-title">{v.attrs.city}</h2>
                </div>
                <div className="modal-address">
                    <span>{v.attrs.address}</span>
                    <span>{v.attrs.zip} {v.attrs.city.split("-")[0]}</span>
                    <span>{v.attrs.phone}</span>
                </div>
                {v.attrs.hasXlService && <div className="store-label">
                    <span>{getTranslation("hasXlService")}</span>
                </div>}
                {v.attrs.onselect && <div className={cleanString("modal-openingtoday", this.getOpenClass(v))}>{m.trust(this.orderTimeStr)}</div>}
                <div className="modal-openinghours">
                    <span className="title h4">{getTranslation("openTimes")}</span>
                    <ul>
                        {this.openHoursStr.map(([d, t]) =>
                            <li className={cleanString(d === this.nowDay && "active", this.isOpen ? "open" : "closed")}>
                                <span>{d}</span>
                                <span>{t}</span>
                            </li>
                        )}
                    </ul>
                </div>
                {v.attrs.onselect && <div className="modal-submit">
                    {v.attrs.isExpressService && v.attrs.isActive
                        ? <a className="btn-secondary" href={v.attrs.navigationUrl}>
                            <span>{getTranslation("chooseThisStore")}</span>
                        </a>
                        : <button type="button" disabled={!v.attrs.isActive} className="btn-secondary" onclick={() => {
                            if (this.openState === "open")
                                v.attrs.onselect()
                            else
                                chooseStoreConfirmDialog(v, { onselect: v.attrs.onselect, open: this.openState })
                        }}>{getTranslation("chooseThisStore")}</button>
                    }
                </div>}
            </div>
        </div >
    }

    getOpenClass(v: m.Vnode<IStoreDialog>) {
        if (this.isOpen) {
            if (v.attrs.isExpressService && !v.attrs.isActive)
                return "closed"
            else
                return "open"
        } else {
            return "closed"
        }
    }

    initMap(v: m.Vnode<IStoreDialog>, el: HTMLElement) {
        const storePosition = new google.maps.LatLng(v.attrs.latitude, v.attrs.longitude)
        const bounds = new google.maps.LatLngBounds()
        bounds.extend(storePosition)

        const map = new google.maps.Map(el, {
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            streetViewControl: false,
            mapTypeControlOptions: { mapTypeIds: [] }
        })

        new google.maps.Marker({
            map,
            position: storePosition,
            icon: {
                url: this.markerStore,
                size: new google.maps.Size(70, 72), // the original size
                scaledSize: new google.maps.Size(45, 46), // the new size you want to use
                anchor: new google.maps.Point(29, 64),
                status: "active"
            }
        })

        if (v.attrs.currentgeo) {
            const geoPosition = new google.maps.LatLng(v.attrs.currentgeo.lat, v.attrs.currentgeo.lng)
            bounds.extend(geoPosition)

            new google.maps.Marker({
                map,
                position: geoPosition,
                icon: {
                    url: this.markerLocation,
                    size: new google.maps.Size(262, 198), // the original size
                    scaledSize: new google.maps.Size(98, 74), // the new size you want to use
                    anchor: new google.maps.Point(49, 59),
                    status: "active"
                }
            })
        }

        google.maps.event.addListener(map, "zoom_changed", () => {
            const sub = google.maps.event.addListener(map, "bounds_changed", () => {
                if (map.getZoom() > 15 && map.initialZoom === true) {
                    map.setZoom(15)
                    map.initialZoom = false
                }
                google.maps.event.removeListener(sub)
            })
        })
        map.initialZoom = true
        map.setCenter(bounds.getCenter())
        map.fitBounds(bounds)
        map.panToBounds(bounds)
    }
}

export const chooseStoreConfirmDialog = (v: m.Vnode<IStoreItem>, { onselect, open }) =>
    dialog({
        dismiss: true,
        animate: true,
        content: close =>
            <div className="tip-dialog">
                {open === "future"
                    ? <div className="tip-dialog-message">
                        <span className="title h4">{getTranslation("nowStillClosed")}</span>
                        <p>{getTranslation("pickupStillClosed")}</p>
                    </div>
                    : <div className="tip-dialog-message">
                        <span className="title h4">{getTranslation("nowClosed")}</span>
                        <p>{getTranslation("pickupNowClosed")}</p>
                    </div>
                }
                <div className="tip-dialog-footer">
                    <button className="btn-default" onclick={() => {
                        close()
                    }}>{getTranslation("pickOtherStore")}</button>
                    {v.attrs.isExpressService && open === "open"
                        ? <a className="btn-default small" href={v.attrs.navigationUrl}>
                            <span>{getTranslation("chooseStore")}</span>
                        </a>
                        : <button className="btn-primary" onclick={() => {
                            close()
                            onselect()
                        }}>{getTranslation("confirmPickStore")}</button>
                    }
                </div>
            </div>
    })